import { useState } from 'react';
import { Mission } from '../../data/missions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FileDrop from '../FileDrop/FileDrop';
import MissionShell, { ErrorNotice, InfoNotice } from '../MissionShell/MissionShell';
import { generateNotesQueries, NotesQueriesSummary } from '../../api/batch4Api';
import { useFileRun } from '../../hooks/useFileRun';
import type { FileResult } from '../../api/files';
import { ProcessingNotice } from '@/components/ui/processing-notice';

const STEPS = [
  'Upload the Account Transactions workbook (.xlsx) and the Mapping workbook (Account Name, Table Name).',
  'Enter the year-end text (printed on the output as "Y.E. <text>").',
  'Click Generate Notes & Queries. Notes_Queries.xlsx downloads automatically.',
];

interface PanelProps {
  mission: Mission;
  onBack: () => void;
}

function NotesQueriesPanel({ mission, onBack }: PanelProps) {
  const [transactions, setTransactions] = useState<File[]>([]);
  const [mapping, setMapping] = useState<File[]>([]);
  const [yearEnd, setYearEnd] = useState('');
  const { status, error, result, fail, run } = useFileRun<FileResult<NotesQueriesSummary>>();

  const handleSubmit = async () => {
    if (transactions.length === 0 || mapping.length === 0) {
      fail('Upload both the Account Transactions and the Mapping files.');
      return;
    }
    await run(() => generateNotesQueries(transactions[0], mapping[0], yearEnd), 'Generating the workbook failed.');
  };

  const s = result?.summary;

  return (
    <MissionShell mission={mission} onBack={onBack} steps={STEPS}>
      <div className="grid gap-4 sm:grid-cols-2">
        <FileDrop
          label="Account Transactions"
          required
          accept={mission.accept}
          hint={mission.maxSizeLabel}
          accentColor={mission.color}
          files={transactions}
          onFiles={setTransactions}
        />
        <FileDrop
          label="Mapping"
          required
          accept={mission.accept}
          hint={mission.maxSizeLabel}
          accentColor={mission.color}
          files={mapping}
          onFiles={setMapping}
        />
      </div>

      <div className="mb-6 rounded-lg border border-border bg-muted p-4">
        <Label htmlFor="nq-ye" className="mb-1.5">
          Y.E. (year end) text
        </Label>
        <Input id="nq-ye" value={yearEnd} onChange={(e) => setYearEnd(e.target.value)} placeholder="e.g. March 2026" />
        <p className="mt-1 text-xs text-muted-foreground">
          Not present in the Account Transactions file, so it is entered here.
        </p>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={status === 'loading'}
        style={{ backgroundColor: mission.color }}
        className="w-full text-white hover:opacity-90"
      >
        {status === 'loading' ? 'Building…' : 'Generate Notes & Queries'}
      </Button>
      {status === 'loading' && <ProcessingNotice label={'Building…'} />}

      {status === 'error' && error && <ErrorNotice message={error} />}
      {status === 'success' && result && s && (
        <div className="mt-4 space-y-3 text-sm">
          <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">✅ {result.filename} downloaded.</div>
          {s.mapping_errors.length > 0 ? (
            <div className="rounded-lg bg-amber-50 px-4 py-3 text-amber-800">
              {s.mapping_errors.length} mapping row(s) were skipped due to errors:
              <ul className="mt-1 list-disc pl-5 text-xs">
                {s.mapping_errors.map((m, i) => (
                  <li key={i}>{m}</li>
                ))}
              </ul>
            </div>
          ) : (
            <div className="rounded-lg bg-green-50 px-4 py-3 text-green-700">Workbook generated with no mapping errors.</div>
          )}
          {s.log && (
            <details className="rounded-lg border border-border bg-muted px-4 py-3 text-muted-foreground">
              <summary className="cursor-pointer text-foreground">Processing log (section build order, balance-check warnings)</summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs">{s.log}</pre>
            </details>
          )}
        </div>
      )}
      {status === 'idle' && <InfoNotice>Upload both files above to enable generation.</InfoNotice>}
    </MissionShell>
  );
}

export default NotesQueriesPanel;
